import React, { useEffect, useMemo, useRef, useState } from 'react'
import arrayMove from 'array-move'
import { findIndex } from 'lodash'
import Icon from '../../../ui/Icon/Icon'
import BlogToolbar from './BlogToolbar'
import BlogTextBlock from './BlogTextBlock'
import BlogCustomBlock from './BlogCustomBlock'
import HeadingLevelSelect from './HeadingLevelSelect'
import { BLOCK_TYPE_OPTIONS, createEmptyBlock, isTextBlock } from './blockFactory'
import styles from './BlogEditor.module.scss'

const blockTypeLabel = (type) =>
  BLOCK_TYPE_OPTIONS.find((option) => option.type === type)?.label || type

/** Insert index 0..n by cursor Y vs block midpoints (standard list reorder). */
function resolveDropIndex(listEl, clientY, blockCount) {
  if (!listEl || blockCount === 0) return 0
  const nodes = listEl.querySelectorAll('[data-ame-block]')
  for (let i = 0; i < nodes.length; i += 1) {
    const rect = nodes[i].getBoundingClientRect()
    if (clientY < rect.top + rect.height / 2) return i
  }
  return blockCount
}

function InsertPlaceholder() {
  return (
    <div className={styles.insertPlaceholder} aria-hidden="true">
      <span className={styles.insertPlaceholderLine} />
      <span className={styles.insertPlaceholderLabel}>Вставити сюди</span>
      <span className={styles.insertPlaceholderLine} />
    </div>
  )
}

function BlogBlockCanvas({
  blocks,
  materialId,
  selectedBlockId,
  onSelectBlock,
  onChangeBlocks,
  onFocusEditor,
  activeEditor,
}) {
  const listRef = useRef(null)
  const dragFromRef = useRef(null)
  const [dropIndex, setDropIndex] = useState(null)
  const [dragBlockIndex, setDragBlockIndex] = useState(null)
  const [paletteDragging, setPaletteDragging] = useState(false)
  const isDragging = dragBlockIndex != null || paletteDragging
  const selectedBlockIndex = findIndex(blocks, { id: selectedBlockId })
  const selectedBlock = useMemo(
    () => blocks[selectedBlockIndex] || null,
    [blocks, selectedBlockIndex]
  )

  useEffect(() => {
    const clearDrag = () => {
      dragFromRef.current = null
      setDropIndex(null)
      setDragBlockIndex(null)
      setPaletteDragging(false)
    }
    window.addEventListener('dragend', clearDrag)
    return () => window.removeEventListener('dragend', clearDrag)
  }, [])

  const updateBlock = (index, nextBlock) => {
    onChangeBlocks((prev) => {
      const next = prev.slice()
      next[index] = nextBlock
      return next
    })
  }

  const moveBlock = (index, delta) => {
    const target = index + delta
    if (target < 0 || target >= blocks.length) return
    onChangeBlocks((prev) => arrayMove(prev, index, target))
  }

  const removeBlock = (index) => {
    onChangeBlocks((prev) => {
      const next = prev.filter((_, i) => i !== index)
      return next.length ? next : [createEmptyBlock('paragraph')]
    })
    onSelectBlock(null)
  }

  const insertBlockAt = (index, type) => {
    const created = createEmptyBlock(type)
    onChangeBlocks((prev) => {
      const next = prev.slice()
      next.splice(index, 0, created)
      return next
    })
    onSelectBlock(created.id)
  }

  const clearDragState = () => {
    dragFromRef.current = null
    setDropIndex(null)
    setDragBlockIndex(null)
    setPaletteDragging(false)
  }

  const setDragImageFromElement = (event, element) => {
    if (!element) return
    const rect = element.getBoundingClientRect()
    const offsetX = Math.max(0, Math.min(event.clientX - rect.left, rect.width))
    const offsetY = Math.max(0, Math.min(event.clientY - rect.top, rect.height))
    event.dataTransfer.setDragImage(element, offsetX, offsetY)
  }

  const normalizeDropIndex = (index) => {
    const from = dragFromRef.current
    if (from == null) return index
    // Same place / immediately after self = no-op, hide indicator
    if (index === from || index === from + 1) return null
    return index
  }

  const onPaletteDragStart = (event, type) => {
    dragFromRef.current = null
    event.dataTransfer.setData('application/blog-block', type)
    event.dataTransfer.effectAllowed = 'copy'
    setDragImageFromElement(event, event.currentTarget)
    window.setTimeout(() => {
      setPaletteDragging(true)
      setDragBlockIndex(null)
    }, 0)
  }

  const onBlockDragStart = (event, index) => {
    dragFromRef.current = index
    event.dataTransfer.setData('application/blog-reorder', String(index))
    event.dataTransfer.effectAllowed = 'move'
    const blockEl = event.currentTarget.closest('[data-ame-block]')
    setDragImageFromElement(event, blockEl)
    window.setTimeout(() => {
      setPaletteDragging(false)
      setDragBlockIndex(index)
    }, 0)
  }

  const onListDragOver = (event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = event.dataTransfer.effectAllowed === 'copy' ? 'copy' : 'move'
    const raw = resolveDropIndex(listRef.current, event.clientY, blocks.length)
    setDropIndex(normalizeDropIndex(raw))
  }

  const onListDrop = (event) => {
    event.preventDefault()
    const raw = resolveDropIndex(listRef.current, event.clientY, blocks.length)
    const index = normalizeDropIndex(raw)
    const blockType = event.dataTransfer.getData('application/blog-block')
    const reorderRaw = event.dataTransfer.getData('application/blog-reorder')
    clearDragState()

    if (index == null) return

    if (blockType) {
      insertBlockAt(index, blockType)
      return
    }

    if (reorderRaw !== '') {
      const from = Number(reorderRaw)
      if (!Number.isInteger(from)) return
      const target = from < index ? index - 1 : index
      onChangeBlocks(arrayMove(blocks, from, target))
    }
  }

  const renderInsertAt = (index) =>
    isDragging && dropIndex === index ? <InsertPlaceholder key={`insert-${index}`} /> : null

  const showBlockSettings = Boolean(selectedBlock && !isTextBlock(selectedBlock))

  return (
    <div className={styles.workspace}>
      <aside className={styles.palette}>
        <p className={styles.paletteTitle}>Блоки</p>
        <div className={styles.paletteList}>
          {BLOCK_TYPE_OPTIONS.map((option) => (
            <button
              key={option.type}
              type="button"
              className={styles.paletteItem}
              aria-label={option.label}
              draggable
              onDragStart={(event) => onPaletteDragStart(event, option.type)}
              onClick={() =>
                insertBlockAt(selectedBlockIndex < 0 ? blocks.length : selectedBlockIndex + 1, option.type)
              }
              title={selectedBlock ? 'Додати під обраним блоком або перетягнути' : 'Додати в кінець або перетягнути'}
            >
              <Icon name={option.icon} weight={option.iconWeight || 'regular'} />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </aside>

      <div className={styles.pageColumn}>
        <BlogToolbar editor={activeEditor} lockBlockquote={selectedBlock?.type === 'blockquote'} />
        <div className={styles.page}>
          <div
            ref={listRef}
            className={`${styles.pageBlocks} ${isDragging ? styles.pageBlocksDragging : ''}`.trim()}
            onDragOver={onListDragOver}
            onDrop={onListDrop}
            onDragLeave={(event) => {
              if (!listRef.current?.contains(event.relatedTarget)) {
                setDropIndex(null)
              }
            }}
          >
            {renderInsertAt(0)}
            {blocks.map((block, index) => (
              <React.Fragment key={block.id}>
                <div
                  data-ame-block
                  className={`${styles.pageBlock} ${
                    selectedBlockId === block.id ? styles.pageBlockSelected : ''
                  } ${dragBlockIndex === index ? styles.pageBlockDragging : ''}`.trim()}
                  onClick={() => onSelectBlock(block.id)}
                  onFocusCapture={() => onSelectBlock(block.id)}
                >
                  <div className={styles.blockChrome}>
                    <span
                      className={styles.dragHandle}
                      title="Перетягнути"
                      draggable
                      onDragStart={(event) => onBlockDragStart(event, index)}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <i />
                      <i />
                      <i />
                      <i />
                      <i />
                      <i />
                    </span>
                    <span className={styles.blockTypeLabel}>{blockTypeLabel(block.type)}</span>
                    {block.type === 'heading' ? (
                      <HeadingLevelSelect
                        value={block.level || 2}
                        onChange={(level) => updateBlock(index, { ...block, level })}
                      />
                    ) : null}
                    <span className={styles.blockChromeSpacer} />
                    <button
                      type="button"
                      className={styles.iconBtn}
                      title="Вгору"
                      disabled={index === 0}
                      onClick={(event) => {
                        event.stopPropagation()
                        moveBlock(index, -1)
                      }}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      title="Вниз"
                      disabled={index === blocks.length - 1}
                      onClick={(event) => {
                        event.stopPropagation()
                        moveBlock(index, 1)
                      }}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtnDanger}
                      title="Видалити"
                      onClick={(event) => {
                        event.stopPropagation()
                        removeBlock(index)
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  <div className={styles.blockBody}>
                    {isTextBlock(block) ? (
                      <BlogTextBlock
                        block={block}
                        onChange={(next) => updateBlock(index, next)}
                        onFocusEditor={onFocusEditor}
                      />
                    ) : (
                      <BlogCustomBlock
                        block={block}
                        materialId={materialId}
                        compact
                        onChange={(next) => updateBlock(index, next)}
                        onFocusEditor={onFocusEditor}
                      />
                    )}
                  </div>
                </div>
                {renderInsertAt(index + 1)}
              </React.Fragment>
            ))}
          </div>
          <button
            type="button"
            className={styles.addAtEnd}
            onClick={() => insertBlockAt(blocks.length, 'paragraph')}
          >
            + Додати текст
          </button>
        </div>
      </div>

      <aside className={styles.selectedSettings}>
        <p className={styles.selectedSettingsTitle}>Налаштування блоку</p>
        {showBlockSettings ? (
          <BlogCustomBlock
            block={selectedBlock}
            materialId={materialId}
            onChange={(next) => {
              const idx = blocks.findIndex((item) => item.id === selectedBlock.id)
              if (idx >= 0) updateBlock(idx, next)
            }}
            onFocusEditor={onFocusEditor}
          />
        ) : (
          <p className={styles.settingsEmpty}>
            Оберіть блок із налаштуваннями (зображення, відео, товари тощо).
          </p>
        )}
      </aside>
    </div>
  )
}

export default BlogBlockCanvas
